import { Injectable } from "@angular/core";
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from "@angular/router";
import { Observable } from "rxjs";
import { AuthService } from "../services/auth.service";
import { ErrorHandlerService } from "src/app/services/errorHandler.service";
import { StorageService } from "src/app/services/storage.service";
import { StatusService } from "src/app/services/status.service";
import { MENSAGEM_SESSAO_EXPIRADA } from "../resources/messages";

export class AuthGuardParams {
  redirectWhenNotLoggedIn: boolean;
  redirectWhenLoggedIn: boolean;
  redirectWhenFirstAccess: boolean;
  redirectWhenNotFirstAccess: boolean;

  static build(params: Partial<AuthGuardParams>) {
    return Object.assign(new AuthGuardParams(), params);
  }

  static loggedOff() {
    return AuthGuardParams.build({
      redirectWhenLoggedIn: true,
    });
  }

  static loggedInAndFirstAccess() {
    return AuthGuardParams.build({
      redirectWhenNotLoggedIn: true,
      redirectWhenNotFirstAccess: true,
    });
  }

  static loggedInAndNotFirstAccess() {
    return AuthGuardParams.build({
      redirectWhenNotLoggedIn: true,
      redirectWhenFirstAccess: true,
    });
  }
}

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private errorHandlerService: ErrorHandlerService,
    private storageService: StorageService,
    private statusService: StatusService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const params = <AuthGuardParams>next.data.authGuardParams;

    if (params.redirectWhenLoggedIn && this.authService.userIsLoggedIn()) {
      this.router.navigate(["/"]);
      return false;
    }

    if (params.redirectWhenNotLoggedIn && !this.authService.userIsLoggedIn()) {
      // Se ainda há dados de sessão no navegador, o usuário estava logado e
      // o token foi perdido/invalidado > avisa em vez de redirecionar calado.
      // No primeiro acesso (sem nada salvo) não mostra a mensagem.
      const haviaSessao =
        localStorage.getItem("usertoken") !== null ||
        localStorage.getItem("config") !== null;

      localStorage.clear();

      if (haviaSessao) {
        this.statusService.setMessage(MENSAGEM_SESSAO_EXPIRADA);
      }

      this.router.navigate(["/auth/login"]);
      return false;
    }

    if (
      params.redirectWhenFirstAccess &&
      this.authService.isUserFirstAccess()
    ) {
      this.router.navigate(["/auth/primeiro-acesso"]);
      return false;
    }

    if (
      params.redirectWhenNotFirstAccess &&
      !this.authService.isUserFirstAccess()
    ) {
      this.router.navigate(["/"]);
      return false;
    }

    return true;
  }
}
